/**
 * NPEContractFormDialog — Create/Edit NPE Contract (Consignment-to-warehouse).
 * 3 tabs: General / Pricing & Storage / Flags & Note (SOP §7.5 Table 53).
 *
 * UI caption phải là "NPE Contract" (KHÔNG dùng "Consignment").
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateNPEContractMutation,
  useUpdateNPEContractMutation,
} from '@/store/api/npeContractsApi'
import {
  useListCertificationsQuery,
  useListDeliveryPlacesQuery,
  useListPackagesQuery,
  useListQualitiesQuery,
  useListTradersQuery,
  useLookupVendorsQuery,
} from '@/store/api/lookupsApi'
import type {
  NPEContractCreateIn,
  NPEContractOut,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'

interface NPEContractFormValues {
  // General
  contract_no: string
  contract_date: string
  cp_code: string
  quality_code: string
  certification: string
  quantity_kg: number | ''
  delivery_time: string
  delivery_place_code: string
  package_code: string
  shipper_code: string
  trader_in_charge: string

  // Pricing & Storage
  stoploss_rate: number | ''
  interest_rate: number | ''
  provisional_market_rate: number | ''
  return_quality: number | ''
  storage_charge: number | ''
  storage_loss: number | ''
  two_way_handling_fee: number | ''
  insurance_fee: number | ''
  interest_rate_note: string
  return_package: string

  // Flags & Note
  is_signed_by_gm: boolean
  is_sent_to_client: boolean
  is_signed_by_client: boolean
  note: string
}

const EMPTY: NPEContractFormValues = {
  contract_no: '',
  contract_date: '',
  cp_code: '',
  quality_code: '',
  certification: '',
  quantity_kg: '',
  delivery_time: '',
  delivery_place_code: '',
  package_code: '',
  shipper_code: '',
  trader_in_charge: '',

  stoploss_rate: '',
  interest_rate: '',
  provisional_market_rate: '',
  return_quality: '',
  storage_charge: '',
  storage_loss: '',
  two_way_handling_fee: '',
  insurance_fee: '',
  interest_rate_note: '',
  return_package: '',

  is_signed_by_gm: false,
  is_sent_to_client: false,
  is_signed_by_client: false,
  note: '',
}

const schema: yup.ObjectSchema<NPEContractFormValues> = yup.object({
  contract_no: yup.string().trim().default('').max(20),
  contract_date: yup.string().required('Bắt buộc nhập ngày hợp đồng'),
  cp_code: yup.string().trim().required('Bắt buộc chọn vendor'),
  quality_code: yup.string().trim().default(''),
  certification: yup.string().trim().default(''),
  quantity_kg: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập quantity', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Quantity phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  delivery_time: yup.string().default(''),
  delivery_place_code: yup.string().trim().default(''),
  package_code: yup.string().trim().default(''),
  shipper_code: yup.string().trim().default(''),
  trader_in_charge: yup.string().trim().default(''),

  stoploss_rate: yup.mixed<number | ''>().default(''),
  interest_rate: yup.mixed<number | ''>().default(''),
  provisional_market_rate: yup.mixed<number | ''>().default(''),
  return_quality: yup.mixed<number | ''>().default(''),
  storage_charge: yup.mixed<number | ''>().default(''),
  storage_loss: yup.mixed<number | ''>().default(''),
  two_way_handling_fee: yup.mixed<number | ''>().default(''),
  insurance_fee: yup.mixed<number | ''>().default(''),
  interest_rate_note: yup.string().default(''),
  return_package: yup.string().default(''),

  is_signed_by_gm: yup.boolean().default(false),
  is_sent_to_client: yup.boolean().default(false),
  is_signed_by_client: yup.boolean().default(false),
  note: yup.string().default(''),
}) as yup.ObjectSchema<NPEContractFormValues>

const TAB_LABELS = ['General', 'Pricing & Storage', 'Flags & Note']

function fromOut(d: NPEContractOut): NPEContractFormValues {
  const n = (x: number | null | undefined): number | '' =>
    x === null || x === undefined ? '' : x
  const s = (x: string | null | undefined): string => x ?? ''
  return {
    contract_no: s(d.contract_no),
    contract_date: s(d.contract_date),
    cp_code: s(d.cp_code),
    quality_code: s(d.quality_code),
    certification: s(d.certification),
    quantity_kg: n(d.quantity_kg),
    delivery_time: s(d.delivery_time),
    delivery_place_code: s(d.delivery_place_code),
    package_code: s(d.package_code),
    shipper_code: s(d.shipper_code),
    trader_in_charge: s(d.trader_in_charge),

    stoploss_rate: n(d.stoploss_rate),
    interest_rate: n(d.interest_rate),
    provisional_market_rate: n(d.provisional_market_rate),
    return_quality: n(d.return_quality),
    storage_charge: n(d.storage_charge),
    storage_loss: n(d.storage_loss),
    two_way_handling_fee: n(d.two_way_handling_fee),
    insurance_fee: n(d.insurance_fee),
    interest_rate_note: s(d.interest_rate_note),
    return_package: s(d.return_package),

    is_signed_by_gm: !!d.is_signed_by_gm,
    is_sent_to_client: !!d.is_sent_to_client,
    is_signed_by_client: !!d.is_signed_by_client,
    note: s(d.note),
  }
}

function toApi(v: NPEContractFormValues): NPEContractCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  return {
    contract_no: str(v.contract_no),
    contract_date: v.contract_date,
    cp_code: v.cp_code.trim(),
    quality_code: str(v.quality_code),
    certification: str(v.certification),
    quantity_kg: typeof v.quantity_kg === 'number' ? v.quantity_kg : 0,
    delivery_time: str(v.delivery_time),
    delivery_place_code: str(v.delivery_place_code),
    package_code: str(v.package_code),
    shipper_code: str(v.shipper_code),
    trader_in_charge: str(v.trader_in_charge),
    note: str(v.note),

    stoploss_rate: num(v.stoploss_rate),
    interest_rate: num(v.interest_rate),
    provisional_market_rate: num(v.provisional_market_rate),
    return_quality: num(v.return_quality),
    storage_charge: num(v.storage_charge),
    storage_loss: num(v.storage_loss),
    two_way_handling_fee: num(v.two_way_handling_fee),
    insurance_fee: num(v.insurance_fee),
    interest_rate_note: str(v.interest_rate_note),
    return_package: str(v.return_package),

    is_signed_by_gm: v.is_signed_by_gm,
    is_sent_to_client: v.is_sent_to_client,
    is_signed_by_client: v.is_signed_by_client,
  }
}

export interface NPEContractFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: NPEContractOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

export function NPEContractFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: NPEContractFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [tab, setTab] = useState(0)
  const [createCt, { isLoading: creating }] = useCreateNPEContractMutation()
  const [updateCt, { isLoading: updating }] = useUpdateNPEContractMutation()
  const loading = creating || updating

  const { data: vendors = [] } = useLookupVendorsQuery({ q: '' })
  const { data: qualities = [] } = useListQualitiesQuery()
  const { data: certifications = [] } = useListCertificationsQuery()
  const { data: places = [] } = useListDeliveryPlacesQuery()
  const { data: packages = [] } = useListPackagesQuery()
  const { data: traders = [] } = useListTradersQuery()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NPEContractFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    setTab(0)
    if (isEdit && initialValue) {
      reset(fromOut(initialValue))
    } else {
      reset(EMPTY)
    }
  }, [open, isEdit, initialValue, reset])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateCt({
            id: initialValue.consignment_p_id,
            body: payload,
          }).unwrap()
          snack.success(`Đã cập nhật NPE Contract ${initialValue.contract_no}`)
          onSaved?.(res.data?.consignment_p_id ?? initialValue.consignment_p_id)
        } else {
          const res = await createCt(payload).unwrap()
          snack.success(`Đã tạo NPE Contract ${res.data?.contract_no ?? ''}`)
          onSaved?.(res.data?.consignment_p_id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, isEdit ? 'Cập nhật thất bại' : 'Tạo NPE Contract thất bại'),
        )
      }
    },
    () => {
      snack.error('Vui lòng kiểm tra các trường còn lỗi (xem các tab)')
    },
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isEdit
          ? `Sửa NPE Contract ${initialValue?.contract_no ?? ''}`
          : 'Tạo NPE Contract'}
      </DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TAB_LABELS.map((l) => (
            <Tab key={l} label={l} />
          ))}
        </Tabs>

        <Box component="form" noValidate sx={{ pt: 2 }}>
          {tab === 0 && (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Controller
                  control={control}
                  name="contract_no"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Số NPE Contract"
                      size="small"
                      sx={{ flex: '1 1 220px' }}
                      disabled={isEdit}
                      placeholder={isEdit ? '' : 'Auto: NPE-XXXXX'}
                      error={!!errors.contract_no}
                      helperText={errors.contract_no?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="contract_date"
                  render={({ field }) => (
                    <DatePicker
                      label="Ngày hợp đồng *"
                      format="DD/MM/YYYY"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { flex: '1 1 200px' },
                          error: !!errors.contract_date,
                          helperText: errors.contract_date?.message,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box display="flex" gap={2} flexWrap="wrap">
                <Controller
                  control={control}
                  name="cp_code"
                  render={({ field }) => {
                    const selected =
                      vendors.find((v) => v.code === field.value) ??
                      (field.value
                        ? { code: field.value, name: '', short_name: null, vat_code: null }
                        : null)
                    return (
                      <Autocomplete
                        size="small"
                        sx={{ flex: '2 1 320px' }}
                        options={vendors}
                        value={selected}
                        onChange={(_e, v) => field.onChange(v?.code ?? '')}
                        getOptionLabel={(opt) => (opt ? `${opt.code} — ${opt.name ?? ''}` : '')}
                        isOptionEqualToValue={(o, v) => o.code === v.code}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Vendor (cp_code) *"
                            error={!!errors.cp_code}
                            helperText={errors.cp_code?.message}
                          />
                        )}
                      />
                    )
                  }}
                />
                <Controller
                  control={control}
                  name="quantity_kg"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value === '' ? '' : field.value}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      label="Quantity (kg) *"
                      type="number"
                      size="small"
                      sx={{ flex: '1 1 180px' }}
                      error={!!errors.quantity_kg}
                      helperText={errors.quantity_kg?.message}
                    />
                  )}
                />
              </Box>

              <Box display="flex" gap={2} flexWrap="wrap">
                <Controller
                  control={control}
                  name="quality_code"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Quality"
                      select
                      size="small"
                      sx={{ flex: '1 1 220px' }}
                      error={!!errors.quality_code}
                      helperText={errors.quality_code?.message}
                    >
                      <MenuItem value="">— Chưa chọn —</MenuItem>
                      {qualities.map((q) => (
                        <MenuItem key={q.code} value={q.code}>
                          {q.code} — {q.description}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  name="certification"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Certification"
                      select
                      size="small"
                      sx={{ flex: '1 1 200px' }}
                      error={!!errors.certification}
                      helperText={errors.certification?.message}
                    >
                      <MenuItem value="">— Chưa chọn —</MenuItem>
                      {certifications.map((c) => (
                        <MenuItem key={c.certification} value={c.certification}>
                          {c.certification}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  name="package_code"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Package"
                      select
                      size="small"
                      sx={{ flex: '1 1 200px' }}
                    >
                      <MenuItem value="">— Chưa chọn —</MenuItem>
                      {packages.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Box>

              <Box display="flex" gap={2} flexWrap="wrap">
                <Controller
                  control={control}
                  name="delivery_time"
                  render={({ field }) => (
                    <DatePicker
                      label="Delivery Time"
                      format="DD/MM/YYYY"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { flex: '1 1 200px' },
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="delivery_place_code"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Delivery Place"
                      select
                      size="small"
                      sx={{ flex: '1 1 220px' }}
                    >
                      <MenuItem value="">— Chưa chọn —</MenuItem>
                      {places.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {p.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  name="trader_in_charge"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Trader in Charge"
                      select
                      size="small"
                      sx={{ flex: '1 1 240px' }}
                    >
                      <MenuItem value="">— Chưa chọn —</MenuItem>
                      {traders.map((t) => (
                        <MenuItem key={t.username} value={t.username}>
                          {t.full_name} ({t.username})
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Box>

              <Controller
                control={control}
                name="shipper_code"
                render={({ field }) => {
                  const selected =
                    vendors.find((v) => v.code === field.value) ??
                    (field.value
                      ? { code: field.value, name: '', short_name: null, vat_code: null }
                      : null)
                  return (
                    <Autocomplete
                      size="small"
                      sx={{ flex: '1 1 320px', maxWidth: 480 }}
                      options={vendors}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.code ?? '')}
                      getOptionLabel={(opt) => (opt ? `${opt.code} — ${opt.name ?? ''}` : '')}
                      isOptionEqualToValue={(o, v) => o.code === v.code}
                      renderInput={(params) => (
                        <TextField {...params} label="Shipper (warehouse holder)" />
                      )}
                    />
                  )
                }}
              />
            </Box>
          )}

          {tab === 1 && (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <NumberField
                  control={control}
                  name="provisional_market_rate"
                  label="Provisional Market Rate"
                />
                <NumberField control={control} name="stoploss_rate" label="Stop-loss Rate" />
                <NumberField control={control} name="interest_rate" label="Interest Rate (%/m)" />
              </Box>

              <Box display="flex" gap={2} flexWrap="wrap">
                <NumberField control={control} name="storage_charge" label="Storage Charge" />
                <NumberField control={control} name="storage_loss" label="Storage Loss" />
                <NumberField control={control} name="return_quality" label="Return Quality" />
              </Box>

              <Box display="flex" gap={2} flexWrap="wrap">
                <NumberField
                  control={control}
                  name="two_way_handling_fee"
                  label="Two-way Handling Fee"
                />
                <NumberField control={control} name="insurance_fee" label="Insurance Fee" />
                <Controller
                  control={control}
                  name="return_package"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Return Package"
                      size="small"
                      sx={{ flex: '1 1 200px' }}
                    />
                  )}
                />
              </Box>

              <Controller
                control={control}
                name="interest_rate_note"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Interest Rate Note"
                    size="small"
                    multiline
                    minRows={2}
                    fullWidth
                  />
                )}
              />
            </Box>
          )}

          {tab === 2 && (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <FormGroup row>
                <Controller
                  control={control}
                  name="is_signed_by_gm"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Signed by GM"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="is_sent_to_client"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Sent to Client"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="is_signed_by_client"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Signed by Client"
                    />
                  )}
                />
              </FormGroup>

              <Controller
                control={control}
                name="note"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ghi chú"
                    size="small"
                    multiline
                    minRows={4}
                    fullWidth
                  />
                )}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Huỷ
        </Button>
        <Button
          onClick={() => void onSubmit()}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Helper — Number field with empty-string fallback. */
function NumberField({
  control,
  name,
  label,
}: {
  control: ReturnType<typeof useForm<NPEContractFormValues>>['control']
  name: keyof NPEContractFormValues
  label: string
}) {
  return (
    <Controller
      control={control}
      // @ts-ignore -- generic name on numeric subset
      name={name}
      render={({ field }) => (
        <TextField
          {...field}
          value={field.value === '' || field.value === null ? '' : field.value}
          onChange={(e) =>
            field.onChange(e.target.value === '' ? '' : Number(e.target.value))
          }
          label={label}
          type="number"
          size="small"
          sx={{ flex: '1 1 180px' }}
        />
      )}
    />
  )
}

export default NPEContractFormDialog
